import type Controller from '@ember/controller';
import Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';

import type ReportRecord from 'webapp/models/report';
import type TemplateRecord from 'webapp/models/template';

// any popup can be open, the template preview has the template
type TemplatePreviewData = Partial<PopupData.Report.Preview.ForTemplate>;

export default class ReportsPublicTemplatesTemplateConnectRoute extends Route {
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('router') declare routerService: Service.Router;

	override beforeModel(): Promise<unknown> {
		// make sure request for templates has finished before we go search for model
		return this.mmTemplatesService.requestReportPublicTemplates();
	}

	override model() {
		return this.modelFor('reports.public-templates.template');
	}

	override afterModel(model: TemplateRecord): void {
		const templateModel = this.mmTemplatesService.createModel(model);

		const afterUse = (report: ReportRecord) => {
			const isTemplatesPath = this.routerService.currentRouteName?.startsWith(
					'reports.public-templates.template',
				),
				activePopup = this.popupService.activePopup,
				isOnSameTemplate =
					report.originateFrom &&
					(activePopup?.data as TemplatePreviewData | undefined)?.template?.objectId ===
						report.originateFrom;

			if (isTemplatesPath && isOnSameTemplate) {
				const reportModel = this.mmReportsService.createModel(report);
				this.navigationService.transitionToRoute('reports.index').then(() => {
					this.mmReportsService.openPreviewPopup(reportModel);
				});
			}
		};

		const onResolveModalClose = () => {
			// is template - reroute after closing
			this.navigationService.transitionToRoute('reports.public-templates.template', model);
		};

		this.mmReportsService.createReportFromTemplate(
			templateModel,
			'report_public_template',
			afterUse,
			onResolveModalClose,
		);
	}

	override setupController(
		controller: Controller,
		model: TemplateRecord,
		transition: PublicTransition,
	): void {
		super.setupController(controller, model, transition);
	}
}
